import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, Download, FileText, Link as LinkIcon, Loader2, MapPin, Megaphone, RefreshCw, UserRound, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, mediaUrl } from '../lib/api';
import type { MessageAttachment as Attachment } from '../types';

/**
 * Rendert einen einzelnen Nachrichten-Anhang.
 *
 * Abruf-Regel:
 *  - Eingehend: nur `mediaAssetId` ist über GET /api/media abrufbar. Fehlt sie
 *    (Worker lädt noch), zeigen wir einen „lädt…"-Platzhalter; bei `downloadError`
 *    einen Fehler-Chip mit „Erneut versuchen".
 *  - Ausgehend: `mediaId` ist bereits die lokale Asset-ID → direkt abrufbar.
 *  - Template-/Location-Anhänge werden eigen dargestellt.
 *  - Button-/Interactive-Antworten stecken bereits im Nachrichtentext → kein Element.
 */
const MEDIA_TYPES = ['image', 'video', 'audio', 'document', 'sticker'];

function resolveAssetId(att: Attachment, outbound: boolean): string | null {
  if (att.mediaAssetId) return att.mediaAssetId;
  if (outbound && att.mediaId) return att.mediaId;
  return null;
}

function isMediaAttachment(att: Attachment): boolean {
  return Boolean(att.mediaId || att.mediaAssetId) || MEDIA_TYPES.includes(att.type);
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function isImage(att: Attachment): boolean {
  return att.type === 'image' || att.type === 'sticker' || (att.mimeType?.startsWith('image/') ?? false);
}
function isVideo(att: Attachment): boolean {
  return att.type === 'video' || (att.mimeType?.startsWith('video/') ?? false);
}
function isAudio(att: Attachment): boolean {
  return att.type === 'audio' || (att.mimeType?.startsWith('audio/') ?? false);
}

export function MessageAttachment({
  attachment,
  outbound,
  messageId,
}: {
  attachment: Attachment;
  outbound: boolean;
  messageId?: string;
}) {
  const [lightbox, setLightbox] = useState(false);
  const retry = useMutation({
    mutationFn: () => {
      if (!messageId) throw new Error('messageId fehlt');
      return api.post(`/api/messages/${messageId}/retry-media`);
    },
    onSuccess: () => toast.info('Anhang wird erneut geladen…'),
    onError: () => toast.error('Erneutes Laden fehlgeschlagen'),
  });

  // Template-Anhänge erscheinen als eigener Hinweis (kein Medium).
  if (attachment.type === 'template') {
    return (
      <div className="mt-1 rounded-md border border-kalkuel-border/60 bg-white/10 px-2 py-1 text-[11px]">
        Vorlage: {attachment.templateName}
      </div>
    );
  }

  // Link-Vorschau (OG-Karte) für eine URL im Nachrichtentext.
  if (attachment.type === 'link_preview' && attachment.url) {
    const host = attachment.siteName || hostnameOf(attachment.url);
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer noopener"
        onClick={(e) => e.stopPropagation()}
        className="mt-1 block w-full max-w-[320px] overflow-hidden rounded-lg border border-kalkuel-border bg-white text-left text-kalkuel-primary no-underline hover:border-kalkuel-accent"
      >
        {attachment.imageAssetId && (
          <img
            src={mediaUrl(attachment.imageAssetId)}
            alt={attachment.title ?? host}
            className="max-h-40 w-full object-cover"
            loading="lazy"
          />
        )}
        <div className="px-2.5 py-1.5">
          {host && (
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-kalkuel-muted">
              <LinkIcon size={10} className="text-kalkuel-accent2" />
              <span className="truncate">{host}</span>
            </div>
          )}
          {attachment.title && (
            <div className="mt-0.5 line-clamp-2 text-xs font-semibold leading-snug">
              {attachment.title}
            </div>
          )}
          {attachment.description && (
            <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-kalkuel-muted">
              {attachment.description}
            </div>
          )}
        </div>
      </a>
    );
  }

  // Click-to-WhatsApp-Werbeanzeige: Anzeigen-Kontext (Headline/Bild/Quell-URL) als Karte.
  if (attachment.type === 'referral') {
    const host = attachment.sourceUrl ? hostnameOf(attachment.sourceUrl) : '';
    // Referral-Quelle: 'post' = organischer Beitrag, sonst (z. B. 'ad') Werbeanzeige.
    const eyebrow = attachment.sourceType === 'post' ? 'Beitrag' : 'Werbeanzeige';
    // Bevorzugt das dauerhaft proxierte MediaAsset (vom unfurl-Worker); fällt auf
    // die kurzlebig signierte Meta-CDN-URL zurück, solange das Proxying noch läuft.
    const referralImageSrc = attachment.imageAssetId
      ? mediaUrl(attachment.imageAssetId)
      : attachment.imageUrl;
    const card = (
      <>
        {referralImageSrc && (
          <img
            src={referralImageSrc}
            alt={attachment.headline ?? eyebrow}
            className="max-h-40 w-full object-cover"
            loading="lazy"
            // Meta-CDN-URLs sind kurzlebig signiert → bei Ablauf das kaputte Bild
            // ausblenden statt einen leeren/zerbrochenen Kasten zu zeigen. Das
            // proxierte Asset ist stabil und löst dieses Problem dauerhaft.
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        <div className="px-2.5 py-1.5">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-kalkuel-accent2">
            <Megaphone size={10} />
            <span className="truncate">{eyebrow}{host ? ` · ${host}` : ''}</span>
          </div>
          {attachment.headline && (
            <div className="mt-0.5 line-clamp-2 text-xs font-semibold leading-snug">{attachment.headline}</div>
          )}
          {attachment.body && (
            <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-kalkuel-muted">{attachment.body}</div>
          )}
        </div>
      </>
    );
    const cls =
      'mt-1 block w-full max-w-[320px] overflow-hidden rounded-lg border border-kalkuel-border bg-white text-left text-kalkuel-primary no-underline hover:border-kalkuel-accent';
    return attachment.sourceUrl ? (
      <a href={attachment.sourceUrl} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()} className={cls}>
        {card}
      </a>
    ) : (
      <div className={cls}>{card}</div>
    );
  }

  // Nicht unterstützter WhatsApp-Typ → sichtbarer Hinweis statt leerer Nachricht.
  if (attachment.type === 'unsupported') {
    return (
      <div className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
        <AlertCircle size={13} />
        Anhang vom Typ „{attachment.waType ?? 'unbekannt'}" wird noch nicht unterstützt
      </div>
    );
  }

  // Geteilte Kontakt-Karte (WhatsApp „Kontakt teilen").
  if (attachment.type === 'contacts') {
    const label = attachment.name || attachment.phone || 'Kontakt';
    const inner = (
      <>
        <UserRound size={13} className="text-kalkuel-accent2" />
        <span className="truncate">
          {label}
          {attachment.name && attachment.phone && (
            <span className="text-kalkuel-muted"> · {attachment.phone}</span>
          )}
        </span>
      </>
    );
    const cls =
      'mt-1 inline-flex items-center gap-1.5 rounded-md border border-kalkuel-border bg-white px-2.5 py-1.5 text-xs text-kalkuel-primary hover:border-kalkuel-accent';
    return attachment.phone ? (
      <a href={`tel:${attachment.phone}`} onClick={(e) => e.stopPropagation()} className={cls}>
        {inner}
      </a>
    ) : (
      <div className={cls}>{inner}</div>
    );
  }

  if (attachment.type === 'location') {
    const { latitude, longitude } = attachment;
    const href =
      latitude != null && longitude != null
        ? `https://www.google.com/maps?q=${latitude},${longitude}`
        : undefined;
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-kalkuel-border bg-white px-2.5 py-1.5 text-xs text-kalkuel-primary hover:border-kalkuel-accent"
      >
        <MapPin size={13} className="text-kalkuel-accent2" />
        Standort
      </a>
    );
  }

  // Button-/Listen-Antworten sind bereits als Nachrichtentext sichtbar → kein Extra-Element.
  if (attachment.type === 'button' || attachment.type === 'interactive') return null;

  // Nicht-Medien-Anhänge ohne darstellbaren Inhalt nicht als Endlos-Spinner zeigen.
  if (!isMediaAttachment(attachment)) return null;

  const assetId = resolveAssetId(attachment, outbound);

  // Eingehendes Medium: Download fehlgeschlagen → Hinweis + erneut versuchen.
  if (!assetId && attachment.downloadError) {
    return (
      <div className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
        <AlertCircle size={13} />
        Anhang nicht ladbar
        {messageId && (
          <button
            type="button"
            onClick={() => retry.mutate()}
            disabled={retry.isPending}
            className="ml-1 inline-flex items-center gap-1 font-medium text-amber-900 underline underline-offset-2 hover:opacity-80 disabled:opacity-50"
          >
            <RefreshCw size={11} className={retry.isPending ? 'animate-spin' : ''} />
            Erneut versuchen
          </button>
        )}
      </div>
    );
  }

  // Eingehendes Medium noch nicht heruntergeladen.
  if (!assetId) {
    return (
      <div className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-kalkuel-border bg-white/60 px-2.5 py-1.5 text-xs text-kalkuel-muted">
        <Loader2 size={13} className="animate-spin" />
        lädt…
      </div>
    );
  }

  const url = mediaUrl(assetId);

  if (isImage(attachment)) {
    return (
      <>
        <button
          type="button"
          onClick={() => setLightbox(true)}
          className="mt-1 block overflow-hidden rounded-md"
        >
          <img
            src={url}
            alt={attachment.filename ?? 'Bild'}
            className="max-h-52 max-w-full rounded-md object-cover"
          />
        </button>
        {attachment.caption && <div className="mt-0.5 text-xs">{attachment.caption}</div>}
        {lightbox && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
            onClick={() => setLightbox(false)}
          >
            <button
              type="button"
              className="absolute right-4 top-4 text-white/80 hover:text-white"
              onClick={() => setLightbox(false)}
              title="Schließen"
            >
              <X size={22} />
            </button>
            <img
              src={url}
              alt={attachment.filename ?? 'Bild'}
              className="max-h-full max-w-full rounded-md"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </>
    );
  }

  if (isVideo(attachment)) {
    return (
      <div className="mt-1">
        <video src={url} controls className="max-h-52 max-w-full rounded-md" />
        {attachment.caption && <div className="mt-0.5 text-xs">{attachment.caption}</div>}
      </div>
    );
  }

  if (isAudio(attachment)) {
    return <audio src={url} controls className="mt-1 max-w-full" />;
  }

  // Dokument / sonstiges → Download-Chip.
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      download={attachment.filename ?? undefined}
      className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-kalkuel-border bg-white px-2.5 py-1.5 text-xs text-kalkuel-primary hover:border-kalkuel-accent"
    >
      <FileText size={13} className="text-kalkuel-accent2" />
      <span className="max-w-[180px] truncate">{attachment.filename ?? 'Datei'}</span>
      <Download size={12} className="text-kalkuel-muted" />
    </a>
  );
}
